import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import { useProgramSummary } from '../../shared/ProgramTree/usePrograms';
import csrfToken from '../../shared/adminCsrf';
import { LessonIcon, ProgramLesson, ProgramLessonsData, ProgramUnit } from './types';

interface RawUnit {
  call_out_color: string | null;
  call_out_text: string | null;
  color: string | null;
  display_number: string;
  has_video: boolean;
  id: number;
  lessons: Array<{ id: number; title: string }>;
  position: number;
  programs_count: number;
  publish: boolean;
  student_text_image_thumb_url: string | null;
  title: string;
  title_number: number | null;
  title_with_position: string;
  unit_program_lesson_id: number;
}

interface RawLesson {
  display_type: string | null;
  games_count: number;
  icon_class: string | null;
  icon_id: number | null;
  id: number;
  image_url: string | null;
  kind: string | null;
  program_lesson_id: number | null;
  materials_count: number;
  notebooks_count: number;
  program_ids: number[];
  slide_shows_count: number;
  states_selected: string;
  title: string;
  title_number: string | null;
  title_with_position: string;
  unit_id: number | null;
}

const useUnits = (programId: number) => useQuery({
  queryFn: async () => {
    const { data } = await axios.get(
      Routes.plato_api_program_units_path(programId, { all_units: true }),
    );
    return (data.data || []) as RawUnit[];
  },
  queryKey: [QueryKeys.ProgramUnits, programId],
});

const useLessons = (programId: number) => useQuery({
  queryFn: async () => {
    const { data } = await axios.get(
      Routes.plato_api_program_lessons_path(programId, {
        extra_attributes: [
          'display_type', 'games_count', 'icon_class', 'icon_id', 'kind', 'materials_count',
          'notebooks_count', 'program_ids', 'program_lesson_id', 'slide_shows_count',
          'states_selected',
        ],
      }),
    );
    return (data.data || []) as RawLesson[];
  },
  queryKey: [QueryKeys.Lessons, programId],
});

// Patches one lesson's fields in the cached list — the shared write path for the inline
// editors and modals, so their saves re-render every subscribed cell without a refetch.
export const usePatchLesson = (programId: number) => {
  const queryClient = useQueryClient();

  return (lessonId: number, patch: Partial<ProgramLesson>) => {
    queryClient.setQueryData<ProgramLesson[]>(
      [QueryKeys.Lessons, programId],
      lessons => (lessons ?? []).map(l => (l.id === lessonId ? { ...l, ...patch } : l)),
    );
  };
};

// Removes a lesson from this program only (its program_lesson row); the lesson itself survives for
// any other program that uses it. The cached list drops the row so the table updates in place.
export const useRemoveLesson = (programId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lesson: ProgramLesson) => axios
      .delete(Routes.plato_api_program_lesson_path(lesson.program_lesson_id))
      .then(() => lesson.id),
    onSuccess: (lessonId) => {
      queryClient.setQueryData<ProgramLesson[]>(
        [QueryKeys.Lessons, programId],
        lessons => (lessons ?? []).filter(l => l.id !== lessonId),
      );
    },
  });
};

// Reorders lessons within one container (a unit's table, the ghost list, or the flat list):
// optimistically rewrites those lessons' slots in the cached full list, then posts the
// container's lesson ids in their new order — same contract as the legacy jQuery sortable.
export const useReorderLessons = (programId: number) => {
  const queryClient = useQueryClient();

  return (orderedRows: ProgramLesson[]) => {
    queryClient.setQueryData<ProgramLesson[]>([QueryKeys.Lessons, programId], (all) => {
      if (!all) return all;

      const ids = new Set(orderedRows.map(lesson => lesson.id));
      let next = 0;
      // eslint-disable-next-line no-plusplus
      return all.map(lesson => (ids.has(lesson.id) ? orderedRows[next++] : lesson));
    });

    return axios
      .post(
        Routes.reorder_admin_program_lessons_path(programId),
        { lesson: orderedRows.map(lesson => lesson.id) },
        { headers: { 'X-CSRF-Token': csrfToken() } },
      )
      .catch((error) => {
        // Restore server truth if the save failed; rethrow so the table skips its saved flash.
        queryClient.invalidateQueries({ queryKey: [QueryKeys.Lessons, programId] });
        throw error;
      });
  };
};

// Reorders the program's units: optimistically rewrites the cached units list, then posts the unit
// ids in their new order to the legacy reorder action — the same contract as its jQuery sortable.
// display_number is left to the server (a set title_number keeps its number), so units are
// refetched once the save lands.
export const useReorderUnits = (programId: number) => {
  const queryClient = useQueryClient();

  return (orderedUnits: ProgramUnit[]) => {
    const key = [QueryKeys.ProgramUnits, programId];
    queryClient.setQueryData<RawUnit[]>(key, (all) => {
      if (!all) return all;

      const byId = new Map(all.map(unit => [unit.id, unit]));
      return orderedUnits.map(unit => byId.get(unit.id)).filter((unit): unit is RawUnit => !!unit);
    });

    return axios
      .post(
        Routes.reorder_admin_program_units_path(programId),
        { unit: orderedUnits.map(unit => unit.id) },
        { headers: { 'X-CSRF-Token': csrfToken() } },
      )
      .then(() => queryClient.invalidateQueries({ queryKey: key }))
      .catch((error) => {
        queryClient.invalidateQueries({ queryKey: key });
        throw error;
      });
  };
};

// Removes a unit from this program through the legacy destroy action: its lessons stay in the
// program without a unit, and the unit record itself goes only when no other program uses it.
// Both caches are patched so the page updates in place.
export const useRemoveUnit = (programId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (unit: ProgramUnit) => axios
      .delete(
        Routes.admin_unit_path(unit.id, { format: 'json', program_id: programId }),
        { headers: { 'X-CSRF-Token': csrfToken() } },
      )
      .then(() => unit.id),
    onSuccess: (unitId) => {
      queryClient.setQueryData<RawUnit[]>(
        [QueryKeys.ProgramUnits, programId],
        units => (units ?? []).filter(unit => unit.id !== unitId),
      );
      queryClient.setQueryData<ProgramLesson[]>(
        [QueryKeys.Lessons, programId],
        lessons => (lessons ?? []).map(l => (l.unit_id === unitId ? { ...l, unit_id: null } : l)),
      );
    },
  });
};

// The lesson-number icon set (7 rows) for the number display popover.
export const useIcons = () => useQuery({
  queryFn: async () => {
    const { data } = await axios.get(Routes.plato_api_icons_path());
    return (data.data || []) as LessonIcon[];
  },
  queryKey: [QueryKeys.Icons],
});

// AIDEV-NOTE: Merges units + lessons APIs client-side. Units provide grouping/headers,
// lessons provide full row data. Lessons with unit_id=null go to ghostLessons.
const useProgramLessons = (programId: number): ProgramLessonsData => {
  const { data: rawUnits = [], isLoading: loadingUnits } = useUnits(programId);
  const { data: rawLessons = [], isLoading: loadingLessons } = useLessons(programId);
  const { color: programColor, isLoading: isLoadingTitle, title: programTitle } = useProgramSummary(programId);

  const { units, ghostLessons, hasUnits } = useMemo(() => {
    const unitCount = rawUnits.length;

    // No units → flat list of all lessons, no grouping needed
    if (unitCount === 0) {
      return { ghostLessons: [], hasUnits: false, units: [] };
    }

    const lessonsByUnit = new Map<number | null, ProgramLesson[]>();

    rawLessons.forEach((lesson) => {
      const group = lessonsByUnit.get(lesson.unit_id) || [];
      group.push(lesson);
      lessonsByUnit.set(lesson.unit_id, group);
    });

    const mergedUnits: ProgramUnit[] = rawUnits.map(unit => ({
      call_out_color: unit.call_out_color,
      call_out_text: unit.call_out_text,
      color: unit.color,
      display_number: unit.display_number,
      has_video: unit.has_video,
      id: unit.id,
      lessons: lessonsByUnit.get(unit.id) || [],
      position: unit.position,
      programs_count: unit.programs_count,
      publish: unit.publish,
      student_text_image_thumb_url: unit.student_text_image_thumb_url,
      title: unit.title,
      title_number: unit.title_number,
      title_with_position: unit.title_with_position,
      unit_program_lesson_id: unit.unit_program_lesson_id,
    }));

    return {
      ghostLessons: lessonsByUnit.get(null) || [],
      hasUnits: true,
      units: mergedUnits,
    };
  }, [rawUnits, rawLessons]);

  return {
    allLessons: rawLessons as ProgramLesson[],
    ghostLessons,
    hasUnits,
    isLoading: loadingUnits || loadingLessons,
    isLoadingTitle,
    programColor,
    programTitle,
    units,
  };
};

export default useProgramLessons;
